import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'
import { isObfuscatedExistingUserResponse } from '@/lib/authResponse'

type SignupRole = 'school' | 'instructor' | 'student'

const ROLE_INFO: Record<SignupRole, { title: string; body: string }> = {
  school:     { title: 'Driving school',        body: 'Multiple instructors, seats & reporting' },
  instructor: { title: 'Individual instructor', body: 'You teach under your own licence' },
  student:    { title: 'Student',               body: 'You were given an invite code' },
}

function mapAuthError(msg: string): string {
  if (msg.includes('already registered') || msg.includes('User already registered'))
    return 'An account with this email already exists. Try signing in instead.'
  if (msg.includes('Password should be at least'))
    return 'Password must be at least 10 characters.'
  if (msg.includes('Invalid email'))
    return 'Please enter a valid email address.'
  return 'Registration failed. Please try again.'
}

interface PwCheck { label: string; pass: boolean }
function getPwChecks(pw: string): PwCheck[] {
  return [
    { label: '10+ characters', pass: pw.length >= 10 },
    { label: 'uppercase',      pass: /[A-Z]/.test(pw) },
    { label: 'lowercase',      pass: /[a-z]/.test(pw) },
    { label: 'number',         pass: /[0-9]/.test(pw) },
    { label: 'symbol',         pass: /[^A-Za-z0-9]/.test(pw) },
  ]
}
function pwStrength(checks: PwCheck[]): number { return checks.filter(c => c.pass).length }

// Shows nothing until the person starts typing, and only lists the requirements still
// UNMET — shrinking toward nothing as the password gets stronger, rather than a fixed
// 5-line checklist that makes "just a password field" look like a form with a dozen
// fields. This is the one thing driving the "the signup screen looks huge" feedback that
// a fixed, always-expanded checklist does to an otherwise 3-field form.
function PasswordStrength({ password }: { password: string }) {
  if (!password) return null
  const checks = getPwChecks(password)
  const unmet = checks.filter(c => !c.pass)
  const strength = pwStrength(checks)
  const barColor = strength <= 2 ? '#E0663B' : strength <= 3 ? '#D4972B' : '#0F7C6C'
  const label = strength <= 1 ? 'Weak' : strength <= 3 ? 'Fair' : strength === 4 ? 'Good' : 'Strong'
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: unmet.length ? 5 : 0 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i <= strength ? barColor : 'rgba(27,42,74,0.1)',
            transition: 'background 0.2s',
          }} />
        ))}
        <div style={{ font: '500 10.5px Inter,sans-serif', color: barColor, flexShrink: 0, marginLeft: 4, lineHeight: '3px' }}>{label}</div>
      </div>
      {unmet.length > 0 && (
        <div style={{ font: '400 11px/1.5 Inter,sans-serif', color: 'rgba(27,42,74,0.45)' }}>
          Needs: {unmet.map(c => c.label).join(' · ')}
        </div>
      )}
    </div>
  )
}

// Signup is deliberately minimal: a lightweight role hint (which of these three cards the
// person picks) plus email/password/confirm-password. Every other field that used to live
// here — school name, MTO licence, instructor phone/licence/service-area, student
// DOB/G1 details, invite code — now belongs to the post-signup onboarding wizard
// (SelectRole.tsx), which runs once the person has a real session, and to Settings for
// anything editable later. This keeps the account-creation step itself fast and the same
// shape for every role, with role-specific depth collected only once it is actually needed.
export default function Register() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialRole = (searchParams.get('role') as SignupRole | null) ?? 'school'

  const [role, setRole] = useState<SignupRole>(initialRole)
  const [existingAccount, setExistingAccount] = useState(false)

  const [email,           setEmail]           = useState('')
  const [password,        setPassword]        = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [terms,           setTerms]           = useState(false)

  const [loading,       setLoading]       = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error,         setError]         = useState('')

  const handleGoogleSignUp = async () => {
    if (googleLoading) return
    setError('')
    setGoogleLoading(true)
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?role=${role}`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      })
      if (oauthError) setError('Google sign-up failed. Please try again.')
    } finally {
      setGoogleLoading(false)
    }
  }

  const validate = (): string | null => {
    if (!email.trim())    return 'Email is required.'
    if (!password.trim()) return 'Password is required.'
    const checks = getPwChecks(password)
    if (!checks.every(c => c.pass)) return 'Password does not meet all requirements.'
    if (password !== confirmPassword) return 'Passwords do not match.'
    if (!terms) return 'You must accept the terms to continue.'
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return

    const validationError = validate()
    if (validationError) { setError(validationError); return }
    setError('')

    setLoading(true)
    try {
      // Create the Supabase auth user with only the role hint as metadata — every other
      // field is collected by the onboarding wizard (SelectRole.tsx) after email
      // confirmation, when a real session exists and RLS-gated writes are possible.
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { primary_role: role === 'school' ? 'school_admin' : role },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      if (signUpError) { setError(mapAuthError(signUpError.message)); return }
      if (!authData.user) { setError('Registration failed. Please try again.'); return }

      // Supabase deliberately returns a 200 with an obfuscated/fake user object — never an
      // error — when signUp() is called for an email that already belongs to a CONFIRMED
      // account (both "Confirm email" and "Confirm phone" enabled on this project, per
      // https://supabase.com/docs/reference/javascript/auth-signup). No new account is
      // created and no confirmation email is sent. identities: [] is the documented
      // discriminator for this case; a genuinely new signup always has at least one
      // identity.
      const isExistingConfirmedUser = isObfuscatedExistingUserResponse(authData.user.identities)
      if (isExistingConfirmedUser) {
        setExistingAccount(true)
        return
      }

      // 2. Hold on check-email page — user must confirm before entering the onboarding
      // wizard. The wizard (SelectRole.tsx) is where role name/school name/instructor
      // phone+licence+service-area/student DOB+G1+invite-code are actually collected.
      localStorage.setItem('dt_signup_role', role)
      localStorage.setItem('dt_signup_email', email.trim())
      navigate('/auth/check-email', { replace: true })

    } finally {
      setLoading(false)
    }
  }

  const submitLabel = {
    school:     'Register your driving school',
    instructor: 'Register as an instructor',
    student:    'Create student account',
  }[role]

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={60} onClick={() => navigate('/')} />
        </div>

        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px',
        }}>
          <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', textAlign: 'center' }}>
            Create your account
          </div>
          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 5, marginBottom: 18 }}>
            Just an email and password to start — everything else comes later
          </div>

          {/* Role picker — a compact segmented control, not three full cards, so it reads
              as "one choice" rather than a form field of its own. */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, background: 'rgba(27,42,74,0.05)', padding: 4, borderRadius: 10 }}>
            {(['school', 'instructor', 'student'] as SignupRole[]).map(r => (
              <div
                key={r}
                onClick={() => setRole(r)}
                style={{
                  flex: 1, cursor: 'pointer', textAlign: 'center', padding: '8px 6px',
                  borderRadius: 7, transition: 'background 0.15s, box-shadow 0.15s',
                  background: role === r ? '#fff' : 'transparent',
                  boxShadow: role === r ? '0 1px 3px rgba(27,42,74,0.12)' : 'none',
                }}
              >
                <div style={{ font: '600 12.5px Inter,sans-serif', color: role === r ? '#0F7C6C' : '#1B2A4A' }}>{ROLE_INFO[r].title}</div>
              </div>
            ))}
          </div>

          {/* Google sign-up */}
          <button
            type='button'
            onClick={handleGoogleSignUp}
            disabled={googleLoading || loading}
            style={{
              width: '100%', padding: '11px 16px', marginBottom: 14,
              background: '#fff', border: '1px solid rgba(27,42,74,0.15)',
              borderRadius: 10, font: '500 14px Inter,sans-serif', color: '#1B2A4A',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 10, opacity: googleLoading ? 0.6 : 1,
            }}
          >
            <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z' fill='#4285F4'/>
              <path d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z' fill='#34A853'/>
              <path d='M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z' fill='#FBBC05'/>
              <path d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z' fill='#EA4335'/>
            </svg>
            {googleLoading ? 'Redirecting…' : 'Continue with Google'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(27,42,74,0.1)' }} />
            <div style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.4)' }}>or</div>
            <div style={{ flex: 1, height: 1, background: 'rgba(27,42,74,0.1)' }} />
          </div>

          {existingAccount ? (
            <div>
              <div style={{ font: '600 16px Lora,serif', color: '#1B2A4A', marginBottom: 8 }}>
                An account with this email already exists
              </div>
              <div style={{ font: '400 13.5px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 18 }}>
                We didn't create a new account or send another confirmation email — this email is already confirmed.
                {role === 'student'
                  ? ' Sign in, then enter your new invite code from Settings → Profile → "Redeem an invite code" — you don\'t need a separate account.'
                  : ' Sign in instead, or reset your password if you\'ve forgotten it.'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Link to='/login' className='btn-primary' style={{ textAlign: 'center', textDecoration: 'none' }}>
                  Sign in
                </Link>
                <Link to='/forgot-password' className='btn-teal' style={{ textAlign: 'center', textDecoration: 'none' }}>
                  Reset password
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FormField label='Email' value={email} onChange={e => setEmail(e.target.value)} placeholder='you@example.com' type='email' autoComplete='email' required />
              <div className='fg2'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <FormField label='Password' value={password} onChange={e => setPassword(e.target.value)} placeholder='Min 10 characters' type='password' autoComplete='new-password' required />
                  <PasswordStrength password={password} />
                </div>
                <FormField label='Confirm password' value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder='Re-enter your password' type='password' autoComplete='new-password' required />
              </div>

              {/* Terms */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div
                  onClick={() => setTerms(!terms)}
                  style={{
                    width: 16, height: 16, borderRadius: 5, flexShrink: 0, marginTop: 1,
                    border: `1.5px solid ${terms ? '#0F7C6C' : 'rgba(27,42,74,0.25)'}`,
                    background: terms ? '#0F7C6C' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', font: '700 10px Inter,sans-serif', cursor: 'pointer',
                  }}
                >
                  {terms ? '✓' : ''}
                </div>
                <div style={{ font: '400 11.5px/1.45 Inter,sans-serif', color: 'rgba(27,42,74,0.55)', userSelect: 'none', cursor: 'pointer' }} onClick={() => setTerms(!terms)}>
                  I agree to the DriveTrack terms and Ontario driver-training record-keeping requirements.
                </div>
              </div>

              {error && <div className='banner-error'>{error}</div>}

              <button type='submit' className='btn-primary' disabled={loading}>
                {loading ? 'Creating account…' : submitLabel}
              </button>

              <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center' }}>
                Already registered?{' '}
                <Link to='/login' style={{ fontWeight: 600, color: '#0F7C6C' }}>Log in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
